/**
 * The follow-up clock — docs/BUSINESS-RULES.md.
 *
 * A job that has gone more than 48 hours without being worked is an alert.
 * "Worked" means a call, a text, an email or a note, in either direction. The
 * clock runs from the job's creation until someone touches it, so a lead
 * nobody ever picked up goes red on its own.
 *
 * A note counts from 2026-08-31 (Jonathon; ADR 0082). It did not before, on
 * the reasoning that writing "left a voicemail" down is not reaching the
 * customer — but the reps work the job in the notes, and a card that stays red
 * after somebody has plainly attended to it is a card they stop believing.
 * What is still excluded is internal chatter: `direction = 'internal'` is
 * staff talking to each other about a job, not to the customer.
 *
 * A follow-up date silences the clock until that day arrives — if the rep has
 * agreed to call back on Friday, Wednesday is not a failure. Once the date is
 * here the clock is live again until a contact is logged or a new date set.
 *
 * The same definition of "a contact" lives in the database as
 * `is_contact_activity()`; see the follow-up clock migration and the one that
 * added notes to it.
 */

import type { ActivityDirection, ActivityType, BidType } from "./types.js";

/** Past this many hours with nothing logged, the card goes red. */
export const FOLLOW_UP_ALERT_HOURS = 48;
/** And amber on the approach, so a job can be saved before it goes red. */
export const FOLLOW_UP_WARN_HOURS = 36;

export const CONTACT_ACTIVITY_TYPES: readonly ActivityType[] = [
  "call",
  "text",
  "email",
  "note",
];

/** Does this activity count as having worked the job? */
export function isContactActivity(activity: {
  type: ActivityType;
  direction?: ActivityDirection | null;
}): boolean {
  return (
    CONTACT_ACTIVITY_TYPES.includes(activity.type) &&
    activity.direction !== "internal"
  );
}

export type FollowUpLevel = "scheduled" | "ok" | "warn" | "alert";

export interface FollowUpStatus {
  level: FollowUpLevel;
  /** Whole hours since the job was last worked, or since it was created. */
  hours: number;
  /** Whether anyone has ever worked this job. */
  everContacted: boolean;
  /** What the pipeline card shows. */
  label: string;
}

export interface FollowUpInput {
  created_at: string;
  last_contact_at?: string | null;
  follow_up_at?: string | null;
}

const HOUR_MS = 3_600_000;

const MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

/** "Aug 5" — short enough for a pipeline card, and locale-independent. */
export function shortDate(value: string | Date): string {
  const date = value instanceof Date ? value : new Date(value);
  return `${MONTHS[date.getMonth()]} ${date.getDate()}`;
}

/** "3h" under two days, "5d" beyond it. Whole units: this is a glance, not a stopwatch. */
export function humanGap(hours: number): string {
  if (hours < 1) return "under an hour";
  if (hours < FOLLOW_UP_ALERT_HOURS) return `${Math.floor(hours)}h`;
  return `${Math.floor(hours / 24)}d`;
}

/**
 * Where a job stands on the follow-up clock.
 *
 * Times come from the database as ISO strings; `now` is injectable so the tests
 * do not depend on the wall clock.
 */
export function followUpStatus(
  job: FollowUpInput,
  now: Date = new Date(),
): FollowUpStatus {
  const everContacted = Boolean(job.last_contact_at);
  const since = new Date(job.last_contact_at ?? job.created_at).getTime();
  const hours = Math.max(0, (now.getTime() - since) / HOUR_MS);

  const followUp = job.follow_up_at ? new Date(job.follow_up_at) : null;
  if (followUp && followUp.getTime() > now.getTime()) {
    return {
      level: "scheduled",
      hours,
      everContacted,
      label: `Follow up ${shortDate(followUp)}`,
    };
  }

  // "No activity" rather than "no contact": a note stops this clock now, and a
  // card that says "no contact" about a job somebody wrote on this morning is a
  // card the reps learn to ignore.
  const gap = humanGap(hours);
  if (hours >= FOLLOW_UP_ALERT_HOURS) {
    return {
      level: "alert",
      hours,
      everContacted,
      label: `${gap} no activity`,
    };
  }
  if (hours >= FOLLOW_UP_WARN_HOURS) {
    return { level: "warn", hours, everContacted, label: `${gap} no activity` };
  }
  return {
    level: "ok",
    hours,
    everContacted,
    label: everContacted ? `Worked ${gap} ago` : `New · ${gap}`,
  };
}

/**
 * Repair or replacement, spelled out.
 *
 * `bid_type` is the stored value and stays as it is; this is what a person
 * reads. "Retail" and "Insurance" describe who pays for a replacement, which is
 * not obvious to someone new to the app.
 */
export const isReplacement = (bidType: BidType): boolean =>
  bidType !== "repair";

export function bidTypeLabel(bidType: BidType): string {
  if (bidType === "repair") return "Repair";
  return bidType === "retail"
    ? "Replacement · Retail"
    : "Replacement · Insurance";
}
